import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, Suspense } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { lazyWithRetry } from "./lib/lazyWithRetry";
import { useTranslation } from "react-i18next";
import { ChatbotProvider } from "@/contexts/ChatbotContext";
import ScrollToTop from "./components/ScrollToTop";
import { findDomainLanding } from "@/data/domainLandings";

// Lazy load UI components that aren't needed for initial paint
const Toaster = lazyWithRetry(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazyWithRetry(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const CookieConsent = lazyWithRetry(() => import("./components/CookieConsent").then(m => ({ default: m.CookieConsent })));

// Lazy load all pages including Index for better code splitting
const Index = lazyWithRetry(() => import("./pages/Index"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const Legal = lazyWithRetry(() => import("./pages/Legal"));
const AboutUs = lazyWithRetry(() => import("./pages/AboutUs"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const AllProperties = lazyWithRetry(() => import("./pages/AllProperties"));
const PropertyTypePage = lazyWithRetry(() => import("./pages/PropertyTypePage"));
const PropertyDetail = lazyWithRetry(() => import("./pages/PropertyDetail"));
const Partners = lazyWithRetry(() => import("./pages/Partners"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogArticle = lazyWithRetry(() => import("./pages/BlogArticle"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const ShowcaseWindow = lazyWithRetry(() => import("./pages/ShowcaseWindow"));
const SeoLandingPage = lazyWithRetry(() => import("./pages/SeoLandingPage"));
const ProgrammaticLandingPage = lazyWithRetry(() => import("./pages/ProgrammaticLandingPage"));
const AdsLandingPage = lazyWithRetry(() => import("./pages/AdsLandingPage"));
const DomainLandingPreview = lazyWithRetry(() => import("./pages/DomainLandingPreview"));

// Admin pages - lazy loaded
const AdminRoute = lazyWithRetry(() => import("./components/AdminRoute"));
const AdminOverview = lazyWithRetry(() => import("./pages/admin/AdminOverview"));
const AdminProperties = lazyWithRetry(() => import("./pages/admin/AdminProperties"));
const AdminPropertyForm = lazyWithRetry(() => import("./pages/admin/AdminPropertyForm"));
const AdminBlog = lazyWithRetry(() => import("./pages/admin/AdminBlog"));
const AdminAnalytics = lazyWithRetry(() => import("./pages/admin/AdminAnalytics"));
const AdminUsers = lazyWithRetry(() => import("./pages/admin/AdminUsers"));
const AdminNewsletter = lazyWithRetry(() => import("./pages/admin/AdminNewsletter"));
const AdminApartments = lazyWithRetry(() => import("./pages/admin/AdminApartments"));
const AdminApartmentForm = lazyWithRetry(() => import("./pages/admin/AdminApartmentForm"));
const AdminSettings = lazyWithRetry(() => import("./pages/admin/AdminSettings"));
const AdminSEO = lazyWithRetry(() => import("./pages/admin/AdminSEO"));
const AdminShowcaseWindows = lazyWithRetry(() => import("./pages/admin/AdminShowcaseWindows"));
const AdminShowcaseWindowForm = lazyWithRetry(() => import("./pages/admin/AdminShowcaseWindowForm"));
const AdminPartners = lazyWithRetry(() => import("./pages/admin/AdminPartners"));
const AdminPartnerForm = lazyWithRetry(() => import("./pages/admin/AdminPartnerForm"));
const AdminHeroMedia = lazyWithRetry(() => import("./pages/admin/AdminHeroMedia"));
const AdminAuditLogs = lazyWithRetry(() => import("./pages/admin/AdminAuditLogs"));
const AdminLeads = lazyWithRetry(() => import("./pages/admin/AdminLeads"));

// Lazy load chatbot
const AIChatbot = lazyWithRetry(() => import("./components/chatbot/AIChatbot"));

// Domain landing page
const DomainLanding = lazyWithRetry(() => import("./pages/DomainLanding"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 min default
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

// Minimal loading fallback - just ensures layout space
const PageLoader = () => (
  <div className="min-h-screen bg-background" />
);

// RTL Direction Handler Component
const RTLHandler = () => {
  const { i18n } = useTranslation();
  useEffect(() => {
    const isRTL = i18n.language?.startsWith('he');
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language || 'en';
    document.documentElement.style.direction = isRTL ? 'rtl' : 'ltr';
  }, [i18n.language]);
  return null;
};

const App = () => {
  // Check if we're on a secondary domain
  const domainLanding = findDomainLanding(window.location.hostname);

  // If on a secondary domain, show the branded landing page
  if (domainLanding) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <DomainLanding landing={domainLanding} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  return (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <ChatbotProvider>
      <TooltipProvider>
        <BrowserRouter basename="/">
          <RTLHandler />
          <Suspense fallback={null}>
            <Toaster />
          </Suspense>
          <Suspense fallback={null}>
            <Sonner />
          </Suspense>
          <Suspense fallback={null}>
            <CookieConsent />
          </Suspense>
          <Suspense fallback={null}>
            <AIChatbot />
          </Suspense>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/legal" element={<Legal />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogArticle />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminOverview />
                </AdminRoute>
              } />
              <Route path="/admin/properties" element={
                <AdminRoute>
                  <AdminProperties />
                </AdminRoute>
              } />
              <Route path="/admin/properties/new" element={
                <AdminRoute>
                  <AdminPropertyForm />
                </AdminRoute>
              } />
              <Route path="/admin/properties/:id/edit" element={
                <AdminRoute>
                  <AdminPropertyForm />
                </AdminRoute>
              } />
              <Route path="/admin/properties/:propertyId/apartments" element={
                <AdminRoute>
                  <AdminApartments />
                </AdminRoute>
              } />
              <Route path="/admin/properties/:propertyId/apartments/new" element={
                <AdminRoute>
                  <AdminApartmentForm />
                </AdminRoute>
              } />
              <Route path="/admin/properties/:propertyId/apartments/:apartmentId/edit" element={
                <AdminRoute>
                  <AdminApartmentForm />
                </AdminRoute>
              } />
              <Route path="/admin/blog" element={
                <AdminRoute>
                  <AdminBlog />
                </AdminRoute>
              } />
              <Route path="/admin/analytics" element={
                <AdminRoute>
                  <AdminAnalytics />
                </AdminRoute>
              } />
              <Route path="/admin/seo" element={
                <AdminRoute>
                  <AdminSEO />
                </AdminRoute>
              } />
              <Route path="/admin/logs" element={
                <AdminRoute>
                  <AdminAuditLogs />
                </AdminRoute>
              } />
              <Route path="/admin/users" element={
                <AdminRoute>
                  <AdminUsers />
                </AdminRoute>
              } />
              <Route path="/admin/newsletter" element={
                <AdminRoute>
                  <AdminNewsletter />
                </AdminRoute>
              } />
              <Route path="/admin/settings" element={
                <AdminRoute>
                  <AdminSettings />
                </AdminRoute>
              } />
              <Route path="/admin/showcases" element={
                <AdminRoute>
                  <AdminShowcaseWindows />
                </AdminRoute>
              } />
              <Route path="/admin/showcases/new" element={
                <AdminRoute>
                  <AdminShowcaseWindowForm />
                </AdminRoute>
              } />
              <Route path="/admin/showcases/:id/edit" element={
                <AdminRoute>
                  <AdminShowcaseWindowForm />
                </AdminRoute>
              } />
              <Route path="/admin/partners" element={
                <AdminRoute>
                  <AdminPartners />
                </AdminRoute>
              } />
              <Route path="/admin/partners/new" element={
                <AdminRoute>
                  <AdminPartnerForm />
                </AdminRoute>
              } />
              <Route path="/admin/partners/:id/edit" element={
                <AdminRoute>
                  <AdminPartnerForm />
                </AdminRoute>
              } />
              <Route path="/admin/hero-media" element={
                <AdminRoute>
                  <AdminHeroMedia />
                </AdminRoute>
              } />
              <Route path="/admin/leads" element={
                <AdminRoute>
                  <AdminLeads />
                </AdminRoute>
              } />
              <Route path="/window/:slug" element={<ShowcaseWindow />} />
              <Route path="/guides/:slug" element={<SeoLandingPage />} />
              {/* Country shortcut URLs — redirect to cornerstone guides */}
              <Route path="/us" element={<Navigate to="/guides/buy-property-israel-from-usa" replace />} />
              <Route path="/us/buy-property-israel" element={<Navigate to="/guides/buy-property-israel-from-usa" replace />} />
              <Route path="/uk" element={<Navigate to="/guides/buy-property-israel-from-uk" replace />} />
              <Route path="/uk/buy-property-israel" element={<Navigate to="/guides/buy-property-israel-from-uk" replace />} />
              <Route path="/fr" element={<Navigate to="/guides/acheter-immobilier-israel-depuis-france" replace />} />
              <Route path="/fr/acheter-immobilier-israel" element={<Navigate to="/guides/acheter-immobilier-israel-depuis-france" replace />} />
              <Route path="/il" element={<Navigate to="/guides/luxury-real-estate-israel-residents" replace />} />
              <Route path="/il/luxury-real-estate" element={<Navigate to="/guides/luxury-real-estate-israel-residents" replace />} />
              {/* Programmatic SEO landing pages: /locations/:country/:type-in-:neighborhood (240 combos) */}
              <Route path="/locations/:country/:slug" element={<ProgrammaticLandingPage />} />
              {/* SEA / paid traffic landing pages — one intent, one CTA */}
              <Route path="/ads/:slug" element={<AdsLandingPage />} />
              <Route path="/preview-domain/:hostname" element={<DomainLandingPreview />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/properties" element={<AllProperties />} />
              <Route path="/property-types/:type" element={<PropertyTypePage />} />
              <Route path="/properties/:slug" element={<PropertyDetail />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </ChatbotProvider>
  </QueryClientProvider>
  </ErrorBoundary>
  );
};

export default App;
